import type { ReactNode } from 'react'

import Button, { ButtonVariant } from '@/components/atomic/button'
import Icon, { PhosphorIconName } from '@/components/atomic/icon'
import Input from '@/components/atomic/input'
import { Select } from '@/components/atomic/select'
import { BodySmall } from '@/components/atomic/text'

export const TargetCardContent = ({
  children,
}: {
  children: React.ReactNode
}) => (
  <div className="w-full flex flex-col gap-2 @container contain-layout">
    {children}
  </div>
)

export const TargetBandContainer = ({
  children,
}: {
  children: React.ReactNode
}) => (
  <div className="w-full flex gap-2 flex-col items-end @min-[500px]:flex-row @min-[500px]:items-start">
    {children}
  </div>
)

export const TargetInputContainer = ({
  children,
}: {
  children: React.ReactNode
}) => <div className="w-3/4 @max-[500px]:w-full">{children}</div>

export enum TargetBandInputType {
  Text = 'text',
  Select = 'select',
}

type TargetBandInputProps = {
  type: TargetBandInputType
  slot: TargetBandSlot
  value?: string
  placeholder?: string
  options?: { label: string; value: string }[]
  disabled?: boolean
  className?: string
  onChange?: (value: string) => void
  onBlur?: (value: string) => void
  constraints?: {
    condition: boolean
    message: string
  }[]
  unitDisplay?: string
}

// biome-ignore lint/style/noDefaultExport: Existing default export — prefer named exports for new code
export default function TargetBandInput(props: TargetBandInputProps) {
  const { type, slot, constraints, disabled, unitDisplay, className } = props

  // Get appropriate step based on unit type
  const getStepValue = () => {
    if (unitDisplay === '$') return '10'
    if (unitDisplay === 'yrs') return '1'
    if (unitDisplay === '%') return '0.001'
    return 'any'
  }

  // it's fine to just return the first non valid constraint
  const error = constraints?.find((c) => !c.condition)
  switch (type) {
    case TargetBandInputType.Text:
      return (
        <Input
          className={`w-full [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${className || ''}`}
          type="number"
          placeholder={props.placeholder ?? 'Enter target'}
          size="small"
          disabled={disabled}
          onChange={(e) => {
            let value = e.target.value

            // Make sure value is positive
            if (Number(value) < 0) {
              value = Math.abs(Number(value)).toString()
            }

            props.onChange?.(value)
          }}
          onBlur={(e) => {
            let value = e.target.value

            // Make sure value is positive
            if (Number(value) < 0) {
              value = Math.abs(Number(value)).toString()
            }

            props.onBlur?.(value)
          }}
          // Set step based on unit type to control decimal precision in the UI
          step={getStepValue()}
          value={props.value ?? ''}
          slotProps={generateSlotProps(slot, type, unitDisplay)}
          error={!!error}
          errorMessage={error ? error.message : undefined}
        />
      )
    case TargetBandInputType.Select:
      if (!props.options) return null
      return (
        <Select
          modal
          wrapperClassName={`w-full ${className || ''}`}
          options={props.options}
          placeholder={props.placeholder ?? 'Select target'}
          onValueChange={props.onChange}
          value={props.value}
          slotProps={generateSlotProps(slot, undefined, unitDisplay)}
          disabled={disabled}
          error={!!error}
          errorMessage={error ? error.message : undefined}
        />
      )
  }
}

enum SlotSide {
  Left = 'left',
  Right = 'right',
  Both = 'both',
}

export enum TargetBandSlot {
  Target = 'target',
  Min = 'min',
  Max = 'max',
  Buffer = 'buffer',
}

function generateSlotProps(
  slot: TargetBandSlot,
  type?: TargetBandInputType,
  unitDisplay?: string,
): {
  slot?: ReactNode
  side: SlotSide
  label?: string
  icon?: ReactNode
  leftIcon?: React.ReactNode
  rightIcon?: React.ReactNode
} {
  const isTextInput = type === TargetBandInputType.Text
  const displayUnit = unitDisplay || '%'

  switch (slot) {
    case TargetBandSlot.Target:
      return {
        side: isTextInput ? SlotSide.Both : SlotSide.Left,
        icon: (
          <div className="flex items-center gap-1">
            <Icon
              isPhosphor
              iconName={PhosphorIconName.TargetIcon}
              className="fill-moment-orange-700"
            />
            <BodySmall className="text-moment-orange-700">Target</BodySmall>
          </div>
        ),
        leftIcon: isTextInput ? (
          <div className="flex items-center gap-1">
            <Icon
              isPhosphor
              iconName={PhosphorIconName.TargetIcon}
              className="fill-moment-orange-700"
            />
            <BodySmall className="text-moment-orange-700">Target</BodySmall>
          </div>
        ) : undefined,
        rightIcon: isTextInput ? (
          <BodySmall className="text-secondary w-4 text-center">
            {displayUnit}
          </BodySmall>
        ) : undefined,
      }
    case TargetBandSlot.Min:
      return {
        side: isTextInput ? SlotSide.Both : SlotSide.Left,
        icon: isTextInput ? undefined : (
          <div className="flex items-center">
            <BodySmall className="text-secondary">Min</BodySmall>
          </div>
        ),
        leftIcon: isTextInput ? (
          <div className="flex items-center">
            <BodySmall className="text-secondary">Min</BodySmall>
          </div>
        ) : undefined,
        rightIcon: isTextInput ? (
          <BodySmall className="text-secondary w-4 text-center">
            {displayUnit}
          </BodySmall>
        ) : undefined,
      }
    case TargetBandSlot.Max:
      return {
        side: isTextInput ? SlotSide.Both : SlotSide.Left,
        icon: isTextInput ? undefined : (
          <div className="flex items-center">
            <BodySmall className="text-secondary">Max</BodySmall>
          </div>
        ),
        leftIcon: isTextInput ? (
          <div className="flex items-center">
            <BodySmall className="text-secondary">Max</BodySmall>
          </div>
        ) : undefined,
        rightIcon: isTextInput ? (
          <BodySmall className="text-secondary w-4 text-center">
            {displayUnit}
          </BodySmall>
        ) : undefined,
      }
    case TargetBandSlot.Buffer:
      return {
        side: isTextInput ? SlotSide.Both : SlotSide.Left,
        icon: isTextInput ? undefined : (
          <div className="flex items-center gap-1">
            <Icon
              isPhosphor
              iconName={PhosphorIconName.PlusMinusIcon}
              className="text-secondary"
            />
            <BodySmall className="text-secondary">Buffer</BodySmall>
          </div>
        ),
        leftIcon: isTextInput ? (
          <div className="flex items-center gap-1">
            <Icon
              isPhosphor
              iconName={PhosphorIconName.PlusMinusIcon}
              className="text-secondary"
            />
            <BodySmall className="text-secondary">Buffer</BodySmall>
          </div>
        ) : undefined,
        rightIcon: isTextInput ? (
          <BodySmall className="text-secondary w-4 text-center">
            {displayUnit}
          </BodySmall>
        ) : undefined,
      }
  }
}

interface ResetButtonProps {
  onClick: () => void
  label?: string
}

export const ResetButton = (props: ResetButtonProps) => {
  return (
    <Button
      variant={ButtonVariant.Ghost}
      onClick={props.onClick}
      label={props.label ?? 'Reset to default'}
      className="text-secondary underline decoration-dotted underline-offset-2 px-1 hover:cursor-pointer hover:text-primary"
    />
  )
}

export const BufferMinMaxContainer = ({
  halfWidth,
  children,
}: {
  halfWidth: boolean
  children: React.ReactNode
}) => (
  <div className={`flex w-full gap-2 ${halfWidth ? 'min-w-1/2' : 'min-w-2/3'}`}>
    {children}
  </div>
)
